import { ApiResponse, PaginatedResponse } from '@/domain'
import { ClientFilters, ClientSummary, UpdateClientData } from '@/domain/clients/client'
import { CreateClientDto } from '@/domain/clients/client-schemas'
import { useToast } from '@/hooks/use-toast'
import { agreementsApi } from '@/rest/modules/agreements-api'
import { clientsApi } from '@/rest/modules/clients-api'
import { parcelsApi } from '@/rest/modules/parcels-api'
import { Paths } from '@/routes/paths'
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (filters: ClientFilters) => [...clientKeys.lists(), { filters }] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
  subscriptions: (id: string) => [...clientKeys.detail(id), 'subscriptions'] as const,
  cremations: (id: string) => [...clientKeys.detail(id), 'cremations'] as const,
  parcels: (id: string) => [...clientKeys.detail(id), 'parcels'] as const,
  stats: () => [...clientKeys.all, 'stats'] as const,
  balance: (id: string) => [...clientKeys.detail(id), 'balance'] as const,
}

export const useClients = (filters?: ClientFilters): UseQueryResult<PaginatedResponse<ClientSummary>, unknown> => {
  return useQuery({
    queryKey: clientKeys.list(filters || {}),
    queryFn: () => clientsApi.getClients(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export const useClient = (id: string) => {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientsApi.getClient(id),
    enabled: !!id,
  })
}

export const useClientsStats = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: clientKeys.stats(),
    queryFn: () => clientsApi.getClientsStats(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  })
}

export const useClientSubscriptions = (id: string) => {
  return useQuery({
    queryKey: clientKeys.subscriptions(id),
    queryFn: () => agreementsApi.getDataSubscriptions({ userId: id, limit: 1000 }),
    enabled: !!id,
  })
}

export const useClientCremations = (id: string) => {
  return useQuery({
    queryKey: clientKeys.cremations(id),
    queryFn: () => agreementsApi.getDataCremations({ userId: id, limit: 1000 }),
    enabled: !!id,
  })
}

export const useClientParcels = (id: string) => {
  return useQuery({
    queryKey: clientKeys.parcels(id),
    queryFn: () => parcelsApi.getParcels({ userId: id, limit: 1000 }),
    enabled: !!id,
  })
}

export const useClientBalance = (id: string) => {
  return useQuery({
    queryKey: clientKeys.balance(id),
    queryFn: () => clientsApi.getClientBalance(id),
    enabled: !!id,
  })
}

export const useCreateClient = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: CreateClientDto) => clientsApi.createClient(data),
    onSuccess: (client) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Cliente creado exitosamente',
        variant: 'default',
      })
      navigate({ to: `${Paths.Management.Clients}/${client.id}` })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: 'Error al crear el cliente',
        variant: 'destructive',
      })
    },
  })
}

export const useUpdateClient = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientData }) =>
      clientsApi.updateClient(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
      toast({
        title: 'Éxito',
        description: 'Cliente actualizado exitosamente',
        variant: 'success',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al actualizar el cliente',
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteClient = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (id: string) => clientsApi.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() })
      toast({
        title: 'Éxito',
        description: 'Cliente eliminado exitosamente',
        variant: 'success',
      })
      navigate({ to: `${Paths.Management.Clients}` })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el cliente',
        variant: 'destructive',
      })
    },
  })
}